/**
 * Image compression utility
 * Compresses images client-side before upload
 */

const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1920;
const QUALITY = 0.8;

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {number} maxSizeMB - Maximum file size in MB (default 5)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, maxSizeMB = 5) {
  return new Promise((resolve, reject) => {
    // If file is already small enough, return as-is
    if (file.size <= maxSizeMB * 1024 * 1024) {
      // Still process to ensure consistent format
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Calculate new dimensions
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            // Create a new file from the blob
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, '.jpg'),
              { type: 'image/jpeg' }
            );

            // If still too large, try with lower quality
            if (compressedFile.size > maxSizeMB * 1024 * 1024) {
              canvas.toBlob(
                (lowerQualityBlob) => {
                  if (lowerQualityBlob) {
                    const lowerQualityFile = new File(
                      [lowerQualityBlob],
                      file.name.replace(/\.[^.]+$/, '.jpg'),
                      { type: 'image/jpeg' }
                    );
                    resolve(lowerQualityFile);
                  } else {
                    resolve(compressedFile);
                  }
                },
                'image/jpeg',
                0.6
              );
            } else {
              resolve(compressedFile);
            }
          },
          'image/jpeg',
          QUALITY
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Compress multiple images
 * @param {FileList|File[]} files - Array of image files
 * @param {number} maxSizeMB - Maximum size per image in MB
 * @returns {Promise<File[]>} - Array of compressed files
 */
export async function compressImages(files, maxSizeMB = 5) {
  const compressedFiles = [];
  
  for (const file of files) {
    try {
      const compressed = await compressImage(file, maxSizeMB);
      compressedFiles.push(compressed);
    } catch (error) {
      console.error(`Failed to compress ${file.name}:`, error);
      // Add original file if compression fails
      compressedFiles.push(file);
    }
  }
  
  return compressedFiles;
}

/**
 * Validate file size
 * @param {File} file 
 * @param {number} maxSizeMB 
 * @returns {boolean}
 */
export function validateFileSize(file, maxSizeMB) {
  return file.size <= maxSizeMB * 1024 * 1024;
}

/**
 * Format file size for display
 * @param {number} bytes 
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
